import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, X, Eye, Trash2, Plus, Filter, MessageSquare } from 'lucide-react';
import { T, fmtMoney, fmtNum, safeNum, healthColor, getGroupConfig } from '../theme';
import { Card, HealthBar, StatusBadge, CoBadge, GroupBadge, ExportBtn, exportRawCSV } from '../components/UI';

const STORAGE_KEY = "cp_monitor_watchlist";
const NOTES_KEY = "cp_monitor_notes";
const ABUSE_FLAGS = ["Scalper","HedgeTrader","NewsTrader","HighFrequencyTrader","HeavyTrader","GainerTrader","LongTrader","IP_Abuser"];
const FLAG_SHORT = { HighFrequencyTrader: "HFT", IP_Abuser: "IP", HedgeTrader: "Hedge", NewsTrader: "News", HeavyTrader: "Heavy", GainerTrader: "Gainer", LongTrader: "Long", Scalper: "Scalper" };

function loadWatchlist() { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; } catch { return []; } }
function saveWatchlist(list) { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); }
function loadNotes() { try { return JSON.parse(localStorage.getItem(NOTES_KEY)) || {}; } catch { return {}; } }
function saveNotes(notes) { localStorage.setItem(NOTES_KEY, JSON.stringify(notes)); }

export function isMonitored(login, company) { return loadWatchlist().some(w => w.login === String(login) && w.company === company); }
export function toggleMonitor(login, company) {
  const wl = loadWatchlist();
  const i = wl.findIndex(w => w.login === String(login) && w.company === company);
  if (i >= 0) wl.splice(i, 1); else wl.push({ login: String(login), company, addedAt: new Date().toISOString() });
  saveWatchlist(wl);
  return wl;
}

// Inline editable note cell
function NoteCell({ clientKey, notes, setNotes }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(notes[clientKey] || "");

  const save = () => {
    const updated = { ...notes };
    if (val.trim()) updated[clientKey] = val.trim(); else delete updated[clientKey];
    saveNotes(updated);
    setNotes(updated);
    setEditing(false);
  };

  if (editing) {
    return (
      <input value={val} onChange={e => setVal(e.target.value)} onBlur={save} onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        autoFocus style={{ width: "100%", padding: "3px 6px", borderRadius: 4, border: `1px solid ${T.cyan}40`, background: T.bg, color: T.text, fontSize: 10, outline: "none" }} />
    );
  }
  const note = notes[clientKey];
  return (
    <div onClick={() => setEditing(true)} title={note || "Click to add note"} style={{ cursor: "pointer", fontSize: 10, color: note ? T.text : T.textMuted + "40", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minHeight: 16, display: "flex", alignItems: "center", gap: 3 }}>
      {note || <><MessageSquare size={10} /> <span style={{ fontStyle: "italic" }}>add note</span></>}
    </div>
  );
}

// ═══ MAIN ═══
export default function Monitoring({ C, onClientClick }) {
  const [watchlist, setWatchlist] = useState(loadWatchlist());
  const [notes, setNotes] = useState(loadNotes());
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [sortBy, setSortBy] = useState("healthScore");
  const [sortDir, setSortDir] = useState("desc");
  const [fStatus, setFStatus] = useState("all");
  const [fGroup, setFGroup] = useState("all");
  const [fAM, setFAM] = useState("all");
  const [fFlag, setFFlag] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const gcfg = getGroupConfig();
  useEffect(() => { setWatchlist(loadWatchlist()); setNotes(loadNotes()); }, []);

  const watchedClients = useMemo(() => {
    const hs = C.healthScores || [];
    return watchlist.map(w => {
      const cl = hs.find(c => String(c.Login) === w.login && c._company === w.company);
      return cl ? { ...cl, _addedAt: w.addedAt } : null;
    }).filter(Boolean);
  }, [watchlist, C.healthScores]);

  const filterOpts = useMemo(() => ({
    statuses: [...new Set(watchedClients.map(c => c.ClientStatus).filter(Boolean))].sort(),
    groups: [...new Set(watchedClients.map(c => c._clientGroup).filter(Boolean))].sort(),
    ams: [...new Set(watchedClients.map(c => c["A.M"]).filter(a => a && a !== "NULL" && a !== "X"))].sort(),
    flags: ABUSE_FLAGS.filter(f => watchedClients.some(c => c[f] === f)),
  }), [watchedClients]);

  const filtered = useMemo(() => {
    let list = watchedClients;
    if (fStatus !== "all") list = list.filter(c => c.ClientStatus === fStatus);
    if (fGroup !== "all") list = list.filter(c => c._clientGroup === fGroup);
    if (fAM !== "all") list = list.filter(c => c["A.M"] === fAM);
    if (fFlag !== "all") list = list.filter(c => c[fFlag] === fFlag);
    return [...list].sort((a, b) => {
      const av = sortBy === "Name" ? (a.Name || "") : safeNum(a[sortBy]);
      const bv = sortBy === "Name" ? (b.Name || "") : safeNum(b[sortBy]);
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [watchedClients, sortBy, sortDir, fStatus, fGroup, fAM, fFlag]);

  const searchResults = useMemo(() => {
    if (!search || search.length < 2) return [];
    const q = search.toLowerCase();
    return (C.healthScores || []).filter(c =>
      String(c.Login).includes(q) || (c.Name || "").toLowerCase().includes(q) || (c.CU_Account || "").toLowerCase().includes(q)
    ).sort((a, b) => (b.healthScore || 0) - (a.healthScore || 0)).slice(0, 20);
  }, [search, C.healthScores]);

  const addClient = useCallback((cl) => { setWatchlist([...toggleMonitor(cl.Login, cl._company)]); }, []);
  const removeClient = useCallback((login, company) => { setWatchlist([...toggleMonitor(login, company)]); }, []);
  const removeAll = useCallback(() => { saveWatchlist([]); setWatchlist([]); }, []);
  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };
  const hasFilters = fStatus !== "all" || fGroup !== "all" || fAM !== "all" || fFlag !== "all";
  const clearFilters = () => { setFStatus("all"); setFGroup("all"); setFAM("all"); setFFlag("all"); };

  const stats = useMemo(() => ({
    risky: watchedClients.filter(c => c.healthScore >= 39).length,
    critical: watchedClients.filter(c => c.healthScore >= 51).length,
    totalFlags: watchedClients.reduce((s, c) => s + ABUSE_FLAGS.filter(f => c[f] === f).length, 0),
  }), [watchedClients]);

  const selSt = { padding: "5px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 11, cursor: "pointer", minWidth: 80 };

  const TH = ({ col, label, style: s }) => (
    <th onClick={() => toggleSort(col)} style={{ padding: "8px 6px", textAlign: "left", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `2px solid ${T.border}`, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none", ...s }}>
      {label} {sortBy === col && <span style={{ fontSize: 7 }}>{sortDir === "asc" ? "▲" : "▼"}</span>}
    </th>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 2 }}>Monitoring & Evaluation</h1>
          <span style={{ color: T.textDim, fontSize: 12 }}>{watchedClients.length} client{watchedClients.length !== 1 ? "s" : ""} under monitoring</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowSearch(!showSearch)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 8, border: "none", background: T.cyan, color: T.bgCard, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            <Plus size={14} /> Add Client
          </button>
          {watchedClients.length > 0 && (
            <button onClick={removeAll}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.red}40`, background: "transparent", color: T.red, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              <Trash2 size={13} /> Clear All
            </button>
          )}
        </div>
      </div>

      {/* Search Panel */}
      {showSearch && (
        <Card accent={T.cyan} style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <Search size={16} color={T.cyan} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by Login, CU, or Name..." autoFocus
              style={{ flex: 1, padding: "10px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: "none" }} />
            <button onClick={() => { setShowSearch(false); setSearch(""); }}
              style={{ padding: "8px 12px", borderRadius: 8, border: "none", background: T.bg, color: T.textMuted, cursor: "pointer" }}><X size={16} /></button>
          </div>
          {search.length >= 2 && <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 6 }}>Sorted by risk score (highest first)</div>}
          {searchResults.length > 0 && (
            <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
              {searchResults.map(cl => {
                const already = watchlist.some(w => w.login === String(cl.Login) && w.company === cl._company);
                return (
                  <div key={(cl._company || "") + cl.Login} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 8, background: already ? T.bg + "60" : T.bg, opacity: already ? 0.5 : 1 }}>
                    <CoBadge co={cl._company} size={16} />
                    <span className="mono" style={{ color: T.cyan, fontWeight: 600, fontSize: 12, width: 60, flexShrink: 0 }}>{cl.Login}</span>
                    <span style={{ fontSize: 12, color: T.text, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cl.Name || "—"}</span>
                    <span className="mono" style={{ fontSize: 10, color: T.textDim, width: 60, flexShrink: 0 }}>{cl.CU_Account || "—"}</span>
                    <span className="mono" style={{ fontSize: 12, color: healthColor(cl.healthScore), fontWeight: 700, width: 26, textAlign: "right", flexShrink: 0 }}>{cl.healthScore}</span>
                    <GroupBadge group={cl._clientGroup} size="sm" client={cl} />
                    <button onClick={() => !already && addClient(cl)} disabled={already}
                      style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: already ? T.border : T.cyan, color: already ? T.textMuted : T.bgCard, fontSize: 10, fontWeight: 700, cursor: already ? "default" : "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                      {already ? "Added" : "+ Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {search.length >= 2 && searchResults.length === 0 && (
            <div style={{ textAlign: "center", padding: 16, color: T.textMuted, fontSize: 12 }}>No clients found for "{search}"</div>
          )}
        </Card>
      )}

      {/* Summary + Filters */}
      {watchedClients.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, padding: "10px 18px", borderRadius: 10, background: T.bgCard, border: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", gap: 16 }}>
            {[
              { label: "Monitored", value: watchedClients.length, color: T.cyan },
              { label: "Critical", value: stats.critical, color: "#ef4444" },
              { label: "High Risk", value: stats.risky, color: "#f97316" },
              { label: "Abuse Flags", value: stats.totalFlags, color: T.orange },
            ].map(s => (
              <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: 4, background: s.color }} />
                <span style={{ fontSize: 11, color: T.textMuted }}>{s.label}</span>
                <span className="mono" style={{ fontSize: 13, fontWeight: 800, color: s.value > 0 ? s.color : T.textMuted }}>{s.value}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 6, border: `1px solid ${hasFilters ? T.cyan : T.border}`, background: hasFilters ? T.cyan + "15" : "transparent", color: hasFilters ? T.cyan : T.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
            <Filter size={12} /> Filters {hasFilters && `(${[fStatus, fGroup, fAM, fFlag].filter(f => f !== "all").length})`}
          </button>
        </div>
      )}

      {showFilters && watchedClients.length > 0 && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 12, padding: "10px 16px", borderRadius: 8, background: T.bgCard, border: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 600 }}>STATUS</span>
            <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={selSt}>
              <option value="all">All</option>
              {filterOpts.statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 600 }}>GROUP</span>
            <select value={fGroup} onChange={e => setFGroup(e.target.value)} style={selSt}>
              <option value="all">All</option>
              {filterOpts.groups.map(gk => <option key={gk} value={gk}>{gk}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 600 }}>A.M</span>
            <select value={fAM} onChange={e => setFAM(e.target.value)} style={selSt}>
              <option value="all">All</option>
              {filterOpts.ams.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 9, color: T.textMuted, fontWeight: 600 }}>FLAG</span>
            <select value={fFlag} onChange={e => setFFlag(e.target.value)} style={selSt}>
              <option value="all">All</option>
              {filterOpts.flags.map(f => <option key={f} value={f}>{FLAG_SHORT[f] || f}</option>)}
            </select>
          </div>
          {hasFilters && <button onClick={clearFilters} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.border}`, background: "transparent", color: T.textMuted, fontSize: 10, cursor: "pointer" }}>✕ Clear</button>}
          <ExportBtn count={filtered.length} onClick={() => exportRawCSV(
            ["Login", "Name", "Company", "Status", "Type", "Risk Score", "Group", "Equity", "Volume", "Deposit", "Notes"],
            filtered.map(c => [c.Login, c.Name || "", c._company || "", c.ClientStatus || "", c.Account_Type || "", c.healthScore || "", c._clientGroup || "", safeNum(c.Equity).toFixed(2), safeNum(c.Volume).toFixed(2), safeNum(c.Deposit).toFixed(2), (notes[c._company + "|" + c.Login] || []).map(n => n.text).join(" | ")]),
            "Monitoring-Watchlist")} />
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: T.textDim }}>{filtered.length} of {watchedClients.length}</span>
        </div>
      )}

      {/* Table */}
      {watchedClients.length > 0 ? (
        <Card>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
              <thead>
                <tr>
                  <th style={{ width: 28, padding: "8px 4px", borderBottom: `2px solid ${T.border}` }}></th>
                  <TH col="Login" label="Login" style={{ width: 65 }} />
                  <TH col="Name" label="Name" style={{ width: 150 }} />
                  <TH col="ClientStatus" label="Status" style={{ width: 72 }} />
                  <TH col="Account_Type" label="Type" style={{ width: 70 }} />
                  <TH col="healthScore" label="Risk" style={{ width: 90, textAlign: "right" }} />
                  <th style={{ width: 70, padding: "8px 6px", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", borderBottom: `2px solid ${T.border}`, textAlign: "center", letterSpacing: 0.5 }}>Group</th>
                  <TH col="Equity" label="Equity" style={{ width: 72, textAlign: "right" }} />
                  <TH col="Volume" label="Vol" style={{ width: 55, textAlign: "right" }} />
                  <TH col="Profit" label="Profit" style={{ width: 72, textAlign: "right" }} />
                  <th style={{ width: 70, padding: "8px 4px", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", borderBottom: `2px solid ${T.border}`, textAlign: "center", letterSpacing: 0.5 }}>Flags</th>
                  <TH col="A.M" label="A.M" style={{ width: 85 }} />
                  <th style={{ width: 130, padding: "8px 6px", fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", borderBottom: `2px solid ${T.border}`, letterSpacing: 0.5 }}>Note</th>
                  <th style={{ width: 24, padding: "8px 2px", borderBottom: `2px solid ${T.border}` }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(cl => {
                  const flags = ABUSE_FLAGS.filter(f => cl[f] === f);
                  const profit = safeNum(cl.Profit);
                  const ck = (cl._company || "") + ":" + cl.Login;
                  return (
                    <tr key={ck} style={{ borderBottom: `1px solid ${T.border}15` }}
                      onMouseEnter={e => e.currentTarget.style.background = T.bgHover}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "8px 4px" }}><CoBadge co={cl._company} size={16} /></td>
                      <td style={{ padding: "8px 6px" }}>
                        <span className="mono" onClick={() => onClientClick?.(cl)} style={{ color: T.cyan, fontWeight: 600, fontSize: 11, cursor: "pointer" }}>{cl.Login}</span>
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        <div onClick={() => onClientClick?.(cl)} style={{ cursor: "pointer" }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{cl.Name || "—"}</div>
                          <div style={{ fontSize: 9, color: T.textMuted }}>{cl.Country || ""}</div>
                        </div>
                      </td>
                      <td style={{ padding: "8px 6px" }}><StatusBadge status={cl.ClientStatus} /></td>
                      <td style={{ padding: "8px 6px", fontSize: 10, color: T.textDim }}>{cl.Account_Type || "—"}</td>
                      <td style={{ padding: "8px 6px", textAlign: "right" }}><HealthBar score={cl.healthScore} /></td>
                      <td style={{ padding: "8px 4px", textAlign: "center" }}><GroupBadge group={cl._clientGroup} size="sm" client={cl} /></td>
                      <td className="mono" style={{ padding: "8px 6px", textAlign: "right", fontSize: 10, color: T.text }}>{fmtMoney(safeNum(cl.Equity))}</td>
                      <td className="mono" style={{ padding: "8px 6px", textAlign: "right", fontSize: 10, color: T.cyan }}>{fmtNum(safeNum(cl.Volume))}</td>
                      <td className="mono" style={{ padding: "8px 6px", textAlign: "right", fontSize: 10, color: profit >= 0 ? T.green : T.red, fontWeight: 600 }}>{fmtMoney(profit)}</td>
                      <td style={{ padding: "8px 4px", textAlign: "center" }}>
                        <div style={{ display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                          {flags.length > 0 ? flags.map(f => (
                            <span key={f} title={f} style={{ padding: "1px 4px", borderRadius: 3, background: T.orange + "20", color: T.orange, fontSize: 8, fontWeight: 700, lineHeight: "13px" }}>
                              {FLAG_SHORT[f]}
                            </span>
                          )) : <span style={{ color: T.textMuted + "40", fontSize: 9 }}>—</span>}
                        </div>
                      </td>
                      <td style={{ padding: "8px 6px", fontSize: 10, color: T.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 85 }}>{(cl["A.M"] || "—").substring(0, 14)}</td>
                      <td style={{ padding: "8px 6px" }}>
                        <NoteCell clientKey={ck} notes={notes} setNotes={setNotes} />
                      </td>
                      <td style={{ padding: "8px 2px", textAlign: "center" }}>
                        <button onClick={() => removeClient(String(cl.Login), cl._company)} title="Remove"
                          style={{ padding: 2, borderRadius: 4, border: "none", background: "transparent", color: T.textMuted, cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.color = T.red} onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>
                          <X size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: "8px 14px", fontSize: 11, color: T.textMuted }}>{filtered.length} monitored client{filtered.length !== 1 ? "s" : ""}</div>
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <Eye size={48} color={T.border} style={{ marginBottom: 16 }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>No clients being monitored</div>
            <div style={{ fontSize: 12, color: T.textMuted, maxWidth: 400, margin: "0 auto 20px" }}>
              Add clients to monitor their risk, group changes, and trading behavior.
            </div>
            <button onClick={() => setShowSearch(true)}
              style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: T.cyan, color: T.bgCard, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              <Plus size={14} style={{ verticalAlign: "middle", marginRight: 6 }} /> Add Your First Client
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}
